import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { cn } from '@/lib/utils';
import fullLogo from '@/assets/logo/dreamoraa_Full_Icon.png';
import singleLogo from '@/assets/logo/dreamoraa_Single_Icon.png';

const SLOGAN = 'LIGHT UP YOUR LIVING';

interface LogoProps {
  /**
   * Variant of logo to display
   * - 'full': Full logo with text (dreamoraa_Full_Icon.png)
   * - 'single': Single icon only (dreamoraa_Single_Icon.png)
   */
  variant?: 'full' | 'single';
  /**
   * Size of the logo
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Whether to show the slogan "LIGHT UP YOUR LIVING" below the logo (full variant only)
   */
  showSlogan?: boolean;
  /**
   * Whether to show as a link to home
   */
  linkToHome?: boolean;
  /**
   * Additional className
   */
  className?: string;
  /**
   * Custom onClick handler (overrides link behavior)
   */
  onClick?: () => void;
}

/**
 * Logo component that displays Dreamoraa branding
 * Supports both full logo and single icon variants
 */
export const Logo = ({
  variant = 'full',
  size = 'md',
  showSlogan = false,
  linkToHome = true,
  className,
  onClick,
}: LogoProps) => {
  const sizeClasses = {
    sm: variant === 'full' ? 'h-8' : 'h-6',
    md: variant === 'full' ? 'h-10' : 'h-8',
    lg: variant === 'full' ? 'h-12' : 'h-10',
  };

  const logoImage = variant === 'full' ? fullLogo : singleLogo;

  const logoElement = (
    <img
      src={logoImage}
      alt="Dreamoraa"
      className={cn('object-contain', sizeClasses[size], className)}
    />
  );

  const withSlogan =
    variant === 'full' && showSlogan ? (
      <div className="relative inline-block">
        <span className="block">{logoElement}</span>
        <span className="absolute left-0 right-0 top-full mt-1.5 text-center text-current text-xs font-semibold tracking-wider uppercase leading-tight sm:text-sm [font-size:0.75rem] sm:[font-size:0.875rem]">
          {SLOGAN}
        </span>
      </div>
    ) : (
      logoElement
    );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
        aria-label="Dreamoraa Logo"
      >
        {withSlogan}
      </button>
    );
  }

  if (linkToHome) {
    return (
      <Link
        to={ROUTES.HOME}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded inline-block"
        aria-label="Dreamoraa Home"
      >
        {withSlogan}
      </Link>
    );
  }

  return withSlogan;
};
